/**
 * Standalone /explore/item/:slug page — same media viewer + detail panel stack as
 * ExploreLightbox, without modal chrome. Feed clicks still open the lightbox;
 * direct navigation to this route renders here.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  Animated,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Image } from 'expo-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import useRequireAuthHook from '@/hooks/useRequireAuth';
import {
  useExploreStore,
  useModelStore,
  useChatInputStore,
  useToastStore,
  useModalStore,
  useAuthStore,
} from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ExploreCard from '@/components/Explore/ExploreCard';
import { exploreApiSsr } from '@/lib/exploreApi';
import { mintExploreImageForVideo, I2V_SLUG_PREFERENCES, resolveImageToVideoModelId } from '@/components/Explore/imageToVideoHelpers';
import { getExploreVideoConfirmCopy } from '@/components/Explore/exploreVideoConfirm';
import { buildExploreSelectedParameters } from '@/components/Explore/exploreVideoParams';
import { notifySuccess, mediumTap } from '@/constants/utils/haptics';
import { getShadowThemeStyles } from '@/constants/Colors';
import { hasPaidCredits, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';
import { classifyResponseError } from '@/constants/utils/response_errors';
import {
  LightboxMedia,
  DetailPanelHeader,
  DetailPanelContent,
  StickyPromptInput,
  VariationsSection,
} from '@/components/Explore/ExploreLightbox';
import { ExploreRouteNavigationHeaderRight } from '@/components/Navigation/Routes/ExploreRouteNavigationOptions';
import AppDashboardFooter from '@/components/AppDashboardFooter';

const PANEL_W = 380;
/** Uniform square tiles in the “More to explore” horizontal rail (+ ghost slot) */
const RELATED_TILE = 200;
/** Matches `ExploreCard` wrapper (`CARD_GAP` 1px) so ghost tile aligns with cards */
const RELATED_CARD_WRAP = RELATED_TILE + 1;
const MAX_CATEGORY_PICKS = 20;
const INITIAL_CATEGORY_VISIBLE = 8;
const isWeb = Platform.OS === 'web';

const truncate = (s, max) => {
  if (!s) return '';
  const t = String(s).trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
};

function ExploreItemFullPage({ bootstrapItem = null } = {}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const isCompact = isXS || isSM;
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const router = useRouter();
  const { slug } = useLocalSearchParams();
  const itemSlug = slug ? String(slug) : '';
  const { height: winH } = useWindowDimensions();

  const fetchItem = useExploreStore(s => s.actions.fetchItem);
  const cloneItem = useExploreStore(s => s.actions.cloneItem);
  const trackExploreEvent = useExploreStore(s => s.actions.trackEvent);
  // The store dual-keys itemsById by both _id and slug, so a slug lookup hits
  // the same shared item ref as the id lookup elsewhere.
  const fromStore = useExploreStore(s => (itemSlug ? s.itemsById[itemSlug] : null));
  const resolvedItem = fromStore || bootstrapItem;

  const fetchCategories = useExploreStore(s => s.actions.fetchCategories);
  const exploreCategories = useExploreStore(s => s.categories);

  const addToast = useToastStore(s => s.actions.addToast);
  const openModal = useModalStore(s => s.actions.openModal);
  const closeModal = useModalStore(s => s.actions.closeModal);
  const isPlus = useAuthStore(s => s.user?.flags?.isPlus);
  const favoriteExploreItemIds = useAuthStore(s => s.user?.favoriteExploreItemIds);
  const toggleFavoriteExploreItem = useAuthStore(s => s.toggleFavoriteExploreItem);
  // favoriteExploreItemIds stores _id strings; check membership by id, fall
  // back to slug for items still en-route through the store.
  const isFavorited = !!(
    (resolvedItem?.id && favoriteExploreItemIds?.includes(resolvedItem.id))
  );
  const heartScaleAnim = useRef(new Animated.Value(1)).current;

  const [isCloning, setIsCloning] = useState(false);
  const [lyricsExpanded, setLyricsExpanded] = useState(false);
  const [remixInput, setRemixInput] = useState('');
  const [activeMediaType, setActiveMediaType] = useState('image');
  const [modelSlug, setModelSlug] = useState(null);
  const [localParams, setLocalParams] = useState({});
  const [musicDescription, setMusicDescription] = useState('');
  const [musicLyrics, setMusicLyrics] = useState('');
  const [relatedItems, setRelatedItems] = useState([]);
  const [categoryPicksExpanded, setCategoryPicksExpanded] = useState(false);

  useEffect(() => {
    if (!itemSlug) return;
    fetchItem(itemSlug);
  }, [itemSlug, fetchItem]);

  useEffect(() => {
    if (!resolvedItem?.slug) return;
    trackExploreEvent(resolvedItem.slug, 'view');
  }, [resolvedItem?.slug, trackExploreEvent]);

  useEffect(() => {
    if (!resolvedItem?.mediaType) return;
    let cancelled = false;
    exploreApiSsr
      .listItems({ page: 1, limit: 36, sort: 'featured', mediaType: resolvedItem.mediaType })
      .then((data) => {
        if (cancelled) return;
        const list = (data.items || []).filter((it) => String(it.slug) !== String(resolvedItem.slug));
        setRelatedItems(list.slice(0, 20));
      })
      .catch(() => { if (!cancelled) setRelatedItems([]); });
    return () => { cancelled = true; };
  }, [resolvedItem?.id, resolvedItem?.mediaType]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const shuffledCategoryLinks = useMemo(() => {
    const list = (exploreCategories || []).filter((c) => c?.slug);
    if (!list.length) return [];
    const shuffled = [...list];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, MAX_CATEGORY_PICKS);
  }, [exploreCategories, resolvedItem?.id]);

  useLayoutEffect(() => {
    const title = resolvedItem
      ? truncate(resolvedItem.displayName || resolvedItem.prompt || 'Explore', 42)
      : 'Explore';
    navigation.setOptions({
      title,
      headerTitle: title,
      headerRight: () => <ExploreRouteNavigationHeaderRight />,
    });
  }, [navigation, resolvedItem]);

  useEffect(() => {
    if (!resolvedItem) return;
    setRemixInput('');
    setActiveMediaType(resolvedItem.mediaType || 'image');
    setModelSlug(resolvedItem.modelSlug || null);
    setLocalParams(resolvedItem.parameters || {});
    setMusicDescription(resolvedItem.musicContext?.description || '');
    setMusicLyrics(resolvedItem.musicContext?.lyrics || '');
    setLyricsExpanded(false);
    setCategoryPicksExpanded(false);
  }, [resolvedItem]);

  useEffect(() => {
    if (!resolvedItem || resolvedItem.mediaType !== 'image') return;
    if (activeMediaType === 'image') {
      setModelSlug(resolvedItem.modelSlug || null);
      setLocalParams(resolvedItem.parameters || {});
      return;
    }
    if (activeMediaType !== 'video') return;
    // Clear image params immediately so a fast Generate can't forward invalid
    // explore durations (e.g. "5"/"10") onto Veo (4/6/8 only).
    setLocalParams({});
    let cancelled = false;
    (async () => {
      const store = useModelStore.getState();
      let pickSlug = null;
      for (const slug of I2V_SLUG_PREFERENCES) {
        if (store.getModelBySlug?.(slug)) { pickSlug = slug; break; }
      }
      if (!pickSlug) {
        const mid = await resolveImageToVideoModelId();
        if (cancelled) return;
        const m = mid ? useModelStore.getState().getModelById(mid) : null;
        pickSlug = m?.slug || null;
      }
      if (cancelled) return;
      if (pickSlug) {
        setModelSlug(pickSlug);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i2v swap keyed by item id + mediaType
  }, [activeMediaType, resolvedItem?.id, resolvedItem?.mediaType, resolvedItem?.modelSlug, resolvedItem?.parameters]);

  const modelSelectorInputs = useMemo(() => {
    if (resolvedItem?.mediaType === 'image' && activeMediaType === 'video') return ['image'];
    return undefined;
  }, [resolvedItem?.mediaType, activeMediaType]);

  // Route gates through the shared `useRequireAuth` so anonymous visitors
  // see the unified `AuthCTAModal` (contextual title/icon) instead of the
  // legacy SignupModal. Returns `true` when the action should bail out.
  const requireAuthShared = useRequireAuthHook();
  const requireAuth = useCallback((action) => {
    const ran = requireAuthShared(() => true, {
      source: `explore_item_${action}`,
      title: action === 'favorite'
        ? 'Sign in to save this'
        : 'Sign in to create with this prompt',
      subtitle: action === 'favorite'
        ? 'Favorites stay tied to your account so they follow you across devices.'
        : 'Free accounts unlock remixing prompts and generating new images, video, music, and 3D.',
      icon: action === 'favorite' ? 'heart' : 'wand-magic-sparkles',
    });
    if (ran !== true) {
      trackEvent('explore', 'explore_auth_required', action);
      return true;
    }
    return false;
  }, [requireAuthShared]);

  const user = useAuthStore(s => s.user);
  const requirePlusFn = useCallback(() => {
    const model = modelSlug
      ? useModelStore.getState().getModelBySlug(modelSlug)
      : null;
    const isVideo = activeMediaType === 'video' || resolvedItem?.mediaType === 'video';
    if (model) {
      if (!isModelLockedForUser(user, model)) return false;
    } else {
      if (user?.flags?.isPlus || hasPaidCredits(user)) return false;
      if (!isVideo && resolvedItem?.mediaType === 'image') return false;
      if (!isVideo && !resolvedItem?.flags?.isPlusShowcase) return false;
    }
    trackEvent('explore', 'explore_upgrade_prompt', modelSlug || resolvedItem?.modelSlug);
    openModelAccessPaywall(openModal, {
      model,
      isVideo,
      title: t('explore.plusRequired'),
      subtitle: t('explore.plusRequiredSubtitle'),
    });
    return true;
  }, [user, openModal, t, modelSlug, activeMediaType, resolvedItem?.mediaType, resolvedItem?.modelSlug, resolvedItem?.flags?.isPlusShowcase]);

  const handleToggleFavorite = useCallback(async () => {
    if (!resolvedItem?.slug) return;
    if (requireAuth('favorite')) return;
    Animated.sequence([
      Animated.spring(heartScaleAnim, { toValue: 1.3, useNativeDriver: true, speed: 30, bounciness: 12 }),
      Animated.spring(heartScaleAnim, { toValue: 1, useNativeDriver: true, speed: 20, bounciness: 8 }),
    ]).start();
    try {
      const nowFavorited = await toggleFavoriteExploreItem(resolvedItem);
      mediumTap();
      trackEvent('explore', nowFavorited ? 'explore_favorite' : 'explore_unfavorite', resolvedItem.slug);
    } catch (_) {
      addToast('Failed to update favorite', 'error');
    }
  }, [resolvedItem, requireAuth, toggleFavoriteExploreItem, addToast, heartScaleAnim]);

  const buildFinalPrompt = useCallback((remix) => {
    const original = (resolvedItem?.prompt || '').trim();
    if (!remix) return original;
    return `${original}\n\n---\nRemix: ${remix}`;
  }, [resolvedItem?.prompt]);

  const runUsePrompt = useCallback(async () => {
    if (isCloning) return;
    setIsCloning(true);
    trackEvent('explore', 'explore_clone', resolvedItem?.slug);
    try {
      const clone = resolvedItem?.slug ? await cloneItem(resolvedItem.slug) : null;
      const store = useChatInputStore.getState();
      const pr = (resolvedItem?.prompt || '').trim();
      const ms = modelSlug;
      // Prefer UI-selected model over clone's source image model.
      let mid = (ms ? useModelStore.getState().getModelBySlug(ms)?.id : null)
        || clone?.model?.id
        || null;
      let attachedFiles = [];
      const isI2V = activeMediaType === 'video' && resolvedItem?.mediaType === 'image';
      if (isI2V) {
        const { file: sourceFile, i2vModelId } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
        const selected = ms ? useModelStore.getState().getModelBySlug(ms) : null;
        const selectedIsI2V = !!(selected?.slug && I2V_SLUG_PREFERENCES.includes(selected.slug));
        mid = i2vModelId
          || (selectedIsI2V ? selected.id : null)
          || await resolveImageToVideoModelId();
        if (!mid) {
          addToast('No image-to-video model is available right now. Please try again later.', 'error');
          return;
        }
      }
      if (activeMediaType === 'video') store.initializeVideoGeneration(pr, mid, true, attachedFiles);
      else if (activeMediaType === 'music') store.initializeMusicGeneration(pr, mid, true, musicDescription || null);
      else if (activeMediaType === '3d') store.initialize3DGeneration(pr, mid, true, false);
      else store.initializeImageGeneration(pr, mid, true);

      const cs = useChatInputStore.getState();
      const ctxId = cs.currentContextId || 'new';
      const is = cs.inputStates?.[ctxId];
      if (is && mid) {
        const mp = buildExploreSelectedParameters(mid, localParams, { ...(is.selectedParameters || {}) });
        useChatInputStore.setState({ inputStates: { ...cs.inputStates, [ctxId]: { ...is, selectedParameters: mp } } });
      }
      if (resolvedItem?.id && cs.pendingAutoSend) {
        useChatInputStore.setState({ pendingAutoSend: { ...useChatInputStore.getState().pendingAutoSend, exploreItemId: resolvedItem.id, exploreItemSlug: resolvedItem.slug } });
      }
      notifySuccess();
      addToast(t('explore.cloneSuccess'), 'success');
      router.push('/app');
    } catch (err) {
      if (err.status === 402 || err.data?.upgradeRequired) {
        const { isModelAccessError } = classifyResponseError({
          actions: err.data?.actions,
          errorMessage: err.message,
          errorCode: err.data?.errorCode,
        });
        if (isModelAccessError || /purchased credits/i.test(err.message || '')) {
          const model = modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null;
          openModelAccessPaywall(openModal, {
            model,
            isVideo: activeMediaType === 'video' || resolvedItem?.mediaType === 'video',
            title: t('explore.plusRequired'),
            subtitle: t('explore.plusRequiredSubtitle'),
          });
        } else {
          openModal('PricingModal', { title: t('explore.limitReached'), subtitle: t('explore.limitReachedSubtitle') });
        }
      } else addToast(err.message || 'Generation failed', 'error');
    } finally { setIsCloning(false); }
  }, [isCloning, resolvedItem, modelSlug, activeMediaType, musicDescription, localParams, cloneItem, addToast, t, router, openModal]);

  const handleUsePrompt = useCallback(() => {
    if (isCloning) return;
    if (requireAuth('generate')) return;
    if (requirePlusFn()) return;

    if (activeMediaType === 'video') {
      const copy = getExploreVideoConfirmCopy(t, modelSlug, localParams);
      openModal('ConfirmationModal', {
        title: copy.title,
        message: copy.message,
        confirmText: copy.confirmText,
        cancelText: copy.cancelText,
        onConfirm: () => {
          closeModal();
          runUsePrompt();
        },
      });
      return;
    }

    runUsePrompt();
  }, [isCloning, requireAuth, requirePlusFn, resolvedItem, activeMediaType, modelSlug, localParams, t, openModal, closeModal, runUsePrompt]);

  const runRemixSubmit = useCallback(async () => {
    if (!remixInput.trim() || isCloning) return;
    setIsCloning(true);
    trackEvent('explore', 'explore_remix_generate', resolvedItem?.slug);
    try {
      const store = useChatInputStore.getState();
      const isCrossMediaRemix = !!resolvedItem?.mediaType && activeMediaType !== resolvedItem.mediaType;
      const isImageToImageRemix = activeMediaType === 'image' && resolvedItem?.mediaType === 'image';
      const usePromptOnly = isCrossMediaRemix || isImageToImageRemix;
      const remixText = remixInput.trim();
      const pr = usePromptOnly ? remixText : buildFinalPrompt(remixText);
      const ms = modelSlug;
      let mid = null;
      if (ms) {
        mid = useModelStore.getState().getModelBySlug(ms)?.id || null;
        if (!mid) {
          try {
            const fetched = await useModelStore.getState().fetchModelBySlug(ms);
            mid = fetched?.id || null;
          } catch { /* noop */ }
        }
      }
      let attachedFiles = [];
      const isI2V = activeMediaType === 'video' && resolvedItem?.mediaType === 'image';
      if (isI2V) {
        const { file: sourceFile, i2vModelId } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
        const selected = ms ? useModelStore.getState().getModelBySlug(ms) : null;
        const selectedIsI2V = !!(selected?.slug && I2V_SLUG_PREFERENCES.includes(selected.slug));
        mid = i2vModelId
          || (selectedIsI2V ? selected.id : null)
          || await resolveImageToVideoModelId();
        if (!mid) {
          addToast('No image-to-video model is available right now. Please try again later.', 'error');
          return;
        }
      } else if (isImageToImageRemix) {
        const { file: sourceFile } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
      }
      if (activeMediaType === 'video') store.initializeVideoGeneration(pr, mid, true, attachedFiles);
      else if (activeMediaType === 'music') store.initializeMusicGeneration(pr, mid, true, musicDescription || null);
      else if (activeMediaType === '3d') store.initialize3DGeneration(pr, mid, true, false);
      else store.initializeImageGeneration(pr, mid, true, attachedFiles);

      const cs = useChatInputStore.getState();
      const ctxId = cs.currentContextId || 'new';
      const is = cs.inputStates?.[ctxId];
      if (is && mid) {
        const mp = buildExploreSelectedParameters(mid, localParams, { ...(is.selectedParameters || {}) });
        useChatInputStore.setState({ inputStates: { ...cs.inputStates, [ctxId]: { ...is, selectedParameters: mp } } });
      }
      notifySuccess();
      addToast(t('explore.remixSuccess'), 'success');
      router.push('/app');
    } catch (err) {
      if (err.status === 402 || err.data?.upgradeRequired) {
        const { isModelAccessError } = classifyResponseError({
          actions: err.data?.actions,
          errorMessage: err.message,
          errorCode: err.data?.errorCode,
        });
        if (isModelAccessError || /purchased credits/i.test(err.message || '')) {
          const model = modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null;
          openModelAccessPaywall(openModal, {
            model,
            isVideo: activeMediaType === 'video' || resolvedItem?.mediaType === 'video',
            title: t('explore.plusRequired'),
            subtitle: t('explore.plusRequiredSubtitle'),
          });
        } else {
          openModal('PricingModal', { title: t('explore.limitReached'), subtitle: t('explore.limitReachedSubtitle') });
        }
      } else addToast(err.message || 'Generation failed', 'error');
    } finally { setIsCloning(false); }
  }, [remixInput, isCloning, resolvedItem, modelSlug, activeMediaType, musicDescription, localParams, buildFinalPrompt, addToast, t, router, openModal]);

  const handleRemixSubmit = useCallback(() => {
    if (!remixInput.trim() || isCloning) return;
    if (requireAuth('generate')) return;
    if (requirePlusFn()) return;

    if (activeMediaType === 'video') {
      const copy = getExploreVideoConfirmCopy(t, modelSlug, localParams);
      openModal('ConfirmationModal', {
        title: copy.title,
        message: copy.message,
        confirmText: copy.confirmText,
        cancelText: copy.cancelText,
        onConfirm: () => {
          closeModal();
          runRemixSubmit();
        },
      });
      return;
    }

    runRemixSubmit();
  }, [remixInput, isCloning, requireAuth, requirePlusFn, resolvedItem, activeMediaType, modelSlug, localParams, t, openModal, closeModal, runRemixSubmit]);

  const handleCategoryPress = useCallback((slug) => {
    if (!slug) {
      router.push('/explore');
      return;
    }
    router.push(`/explore/category/${encodeURIComponent(slug)}`);
  }, [router]);

  const handleGoToExplore = useCallback(() => {
    router.push('/explore');
  }, [router]);

  const handleModelPress = useCallback((slug) => {
    if (!slug) return;
    router.push(`/explore?model=${encodeURIComponent(slug)}`);
  }, [router]);

  const handleCopyPrompt = useCallback(async () => {
    if (!resolvedItem?.prompt) return;
    trackEvent('explore', 'explore_copy_prompt', resolvedItem.slug);
    try {
      const copyTextMod = await import('@/constants/utils/copy_text');
      await copyTextMod.default(resolvedItem.prompt);
      mediumTap();
      addToast('Prompt copied', 'success');
    } catch {
      addToast('Failed to copy', 'error');
    }
  }, [resolvedItem, addToast]);

  const handleDownload = useCallback(() => {
    if (!resolvedItem?.slug) return;
    trackEvent('explore', 'explore_download', resolvedItem.slug);
    trackExploreEvent(resolvedItem.slug, 'download');
  }, [resolvedItem, trackExploreEvent]);

  const handleRelatedPress = useCallback((item) => {
    if (!item?.slug) return;
    router.push(`/explore/item/${encodeURIComponent(item.slug)}`);
  }, [router]);

  const handleVariationSelect = useCallback((next) => {
    if (!next?.slug) return;
    trackEvent('explore', 'explore_navigate_variation_chain', next.slug);
    router.replace(`/explore/item/${encodeURIComponent(next.slug)}`);
    fetchItem(next.slug);
  }, [router, fetchItem]);

  const panelBg = getShadowThemeStyles(theme).primaryOpaque(0.98);

  const detailPanel = resolvedItem ? (
    <View
      style={[
        styles.rightColumn,
        {
          width: isCompact ? '100%' : PANEL_W,
          maxWidth: isCompact ? '100%' : PANEL_W,
          backgroundColor: panelBg,
          borderColor: colors.border_color_secondary,
        },
      ]}
    >
      <DetailPanelHeader
        resolvedItem={resolvedItem}
        colors={colors}
        scrolled={false}
        onModelPress={handleModelPress}
        isCompact={isCompact}
        isExpanded={false}
        onToggleExpand={() => {}}
      />
      <View style={[styles.panelScroll, styles.panelScrollContent]}>
        <DetailPanelContent
          key={resolvedItem.slug || resolvedItem.id}
          resolvedItem={resolvedItem}
          colors={colors}
          activeMediaType={activeMediaType}
          setActiveMediaType={setActiveMediaType}
          modelSlug={modelSlug}
          setModelSlug={setModelSlug}
          localParams={localParams}
          setLocalParams={setLocalParams}
          musicDescription={musicDescription}
          setMusicDescription={setMusicDescription}
          musicLyrics={musicLyrics}
          setMusicLyrics={setMusicLyrics}
          lyricsExpanded={lyricsExpanded}
          setLyricsExpanded={setLyricsExpanded}
          handleCopyPrompt={handleCopyPrompt}
          isPlus={isPlus}
          onCategoryPress={handleCategoryPress}
          onDownload={handleDownload}
          modelSelectorInputs={modelSelectorInputs}
          relatedNavMode="page"
          hideVariationsSection
        />
      </View>
      <StickyPromptInput
        remixInput={remixInput}
        setRemixInput={setRemixInput}
        mediaType={activeMediaType}
        setMediaType={setActiveMediaType}
        onRemix={handleRemixSubmit}
        onUsePrompt={handleUsePrompt}
        isSubmitting={isCloning}
        colors={colors}
        itemMediaType={resolvedItem.mediaType}
        clones={resolvedItem.stats?.clones || 0}
      />
    </View>
  ) : null;

  const mediaStageHeight = useMemo(() => {
    const target = 500;
    if (!winH) return isCompact ? Math.min(360, target) : target;
    if (isCompact) return Math.min(target, Math.max(220, Math.round(winH * 0.42)));
    return Math.min(target, Math.max(320, winH - 140));
  }, [winH, isCompact]);

  const mediaBlock = resolvedItem ? (
    <View style={[styles.mediaColumn, isCompact && styles.mediaColumnCompact]}>
      <View
        style={[
          styles.mediaInner,
          {
            backgroundColor: resolvedItem.blurhash ? 'transparent' : colors.solid_color_tertiary,
            height: mediaStageHeight,
            maxHeight: mediaStageHeight,
          },
        ]}
      >
        {resolvedItem.blurhash ? (
          <View style={StyleSheet.absoluteFillObject} pointerEvents="none">
            <Image
              source={null}
              placeholder={{ blurhash: resolvedItem.blurhash }}
              style={[StyleSheet.absoluteFillObject, { transform: [{ scale: 1.12 }] }]}
              contentFit="cover"
              transition={0}
            />
            <View style={[StyleSheet.absoluteFillObject, styles.mediaBlurhashScrim]} />
          </View>
        ) : null}
        <View style={styles.mediaForeground}>
          <LightboxMedia item={resolvedItem} colors={colors} />
        </View>
      </View>
      {Array.isArray(resolvedItem.children) && resolvedItem.children.length > 0 ? (
        <View style={styles.variationsBelowMedia}>
          <VariationsSection
            variations={resolvedItem.children}
            colors={colors}
            onSelect={handleVariationSelect}
            tileSize={200}
            titleTextProps={{
              size: 's',
              weight: 'semibold',
              color: 'tertiary',
              style: styles.exploreItemSectionTitle,
            }}
          />
        </View>
      ) : null}
      <Pressable
        onPress={handleToggleFavorite}
        style={[styles.favoriteFab, { top: Math.max(12, insets.top + 6) }]}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={isFavorited ? 'Remove from favorites' : 'Save to favorites'}
      >
        <Animated.View style={{ transform: [{ scale: heartScaleAnim }] }}>
          <Icon name="heart" size={18} color={isFavorited ? '#ef4444' : colors.text_color_primary} solid={isFavorited} />
        </Animated.View>
      </Pressable>
    </View>
  ) : null;

  if (!itemSlug) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.solid_color_primary }]}>
        <Text color="secondary">Missing item slug.</Text>
      </View>
    );
  }

  if (!resolvedItem) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.solid_color_primary }]}>
        <Text color="secondary">{t('common.loading')}</Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.pageScroll, { backgroundColor: colors.solid_color_primary }]}
      contentContainerStyle={[styles.pageContent, { paddingBottom: insets.bottom + 24 }]}
      keyboardShouldPersistTaps="handled"
    >
      {isCompact ? (
        <View style={styles.compactStack}>
          {mediaBlock}
          {detailPanel}
        </View>
      ) : (
        <View style={styles.desktopRow}>
          {mediaBlock}
          {detailPanel}
        </View>
      )}

      <View style={styles.relatedSection}>
        <Text size="s" weight="semibold" color="tertiary" style={styles.exploreItemSectionTitle}>
          {t('explore.relatedItems')}
        </Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.relatedScroll}
          contentContainerStyle={styles.relatedRowContent}
        >
          {relatedItems.map((it) => (
            <ExploreCard
              key={String(it.slug || it.id)}
              item={it}
              onPress={handleRelatedPress}
              squareTile
              squareSize={RELATED_TILE}
            />
          ))}
          <View style={[styles.relatedGhostWrap, { width: RELATED_CARD_WRAP }]}>
            <Pressable
              onPress={handleGoToExplore}
              style={({ pressed }) => [
                styles.relatedExploreGhost,
                {
                  width: RELATED_TILE,
                  height: RELATED_TILE,
                  borderColor: colors.text_color_tertiary,
                  backgroundColor: pressed ? colors.solid_color_tertiary : 'transparent',
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={t('explore.goToExplorePage')}
            >
              <Icon name="compass" size={28} color={colors.text_color_tertiary} />
              <Text size="xs" weight="medium" color="secondary" style={styles.relatedGhostLabel}>
                {t('explore.goToExplorePage')}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </View>

      <View style={styles.categoryPicksSection}>
        <Text size="s" weight="semibold" color="tertiary" style={styles.exploreItemSectionTitle}>
          {t('explore.browseCategories')}
        </Text>
        <View style={styles.categoryChipsRow}>
          {(categoryPicksExpanded
            ? shuffledCategoryLinks
            : shuffledCategoryLinks.slice(0, INITIAL_CATEGORY_VISIBLE)
          ).map((cat) => (
            <Pressable
              key={cat.slug}
              onPress={() => handleCategoryPress(cat.slug)}
              style={({ pressed }) => [
                styles.categoryChip,
                {
                  borderColor: colors.text_color_secondary,
                  backgroundColor: pressed ? colors.solid_color_tertiary : 'transparent',
                },
              ]}
              accessibilityRole="link"
              accessibilityLabel={cat.name || cat.slug}
            >
              <Text size="s" color="primary" numberOfLines={1}>
                {cat.name || cat.slug}
              </Text>
            </Pressable>
          ))}
          {shuffledCategoryLinks.length > INITIAL_CATEGORY_VISIBLE ? (
            <Pressable
              onPress={() => setCategoryPicksExpanded((v) => !v)}
              style={({ pressed }) => [
                styles.categoryMoreChip,
                {
                  borderColor: colors.text_color_primary,
                  backgroundColor: pressed ? colors.solid_color_tertiary : colors.solid_color_secondary,
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={categoryPicksExpanded ? t('common.less') : t('common.more')}
            >
              <Text size="s" weight="semibold" color="primary">
                {categoryPicksExpanded ? t('common.less') : t('common.more')}
              </Text>
            </Pressable>
          ) : null}
          <Pressable
            onPress={handleGoToExplore}
            style={({ pressed }) => [
              styles.categoryExploreGhost,
              {
                borderColor: colors.text_color_tertiary,
                backgroundColor: pressed ? colors.solid_color_tertiary : 'transparent',
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('explore.goToExplorePage')}
          >
            <Icon name="compass" size={14} color={colors.text_color_tertiary} />
            <Text size="s" weight="medium" color="tertiary" numberOfLines={1}>
              {t('explore.goToExplorePage')}
            </Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.footerSlot}>
        <AppDashboardFooter />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  pageScroll: {
    flex: 1,
    width: '100%',
  },
  pageContent: {
    width: '100%',
    maxWidth: 1460,
    alignSelf: 'center',
    paddingHorizontal: isWeb ? 16 : 12,
    paddingTop: 12,
    gap: 20,
  },
  desktopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
  },
  compactStack: {
    width: '100%',
    gap: 12,
  },
  mediaColumn: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
  },
  mediaColumnCompact: {
    alignSelf: 'stretch',
  },
  mediaInner: {
    width: '100%',
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mediaBlurhashScrim: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  mediaForeground: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  variationsBelowMedia: {
    width: '100%',
    marginTop: 12,
    alignSelf: 'stretch',
  },
  favoriteFab: {
    position: 'absolute',
    right: 12,
    zIndex: 5,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb ? { boxShadow: '0 1px 4px rgba(0,0,0,0.12)' } : {}),
  },
  rightColumn: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
    flexShrink: 0,
  },
  panelScroll: {
    width: '100%',
  },
  panelScrollContent: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 8,
    gap: 14,
  },
  relatedSection: {
    width: '100%',
    marginTop: 8,
    gap: 10,
  },
  exploreItemSectionTitle: {
    letterSpacing: 0.5,
    marginTop: 4,
  },
  relatedScroll: {
    width: '100%',
    flexGrow: 0,
  },
  relatedRowContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 4,
    paddingRight: 8,
  },
  relatedGhostWrap: {
    padding: 0.5,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  relatedExploreGhost: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  relatedGhostLabel: {
    textAlign: 'center',
    paddingHorizontal: 6,
  },
  categoryPicksSection: {
    width: '100%',
    gap: 10,
    marginTop: 4,
  },
  categoryChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  categoryChip: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    maxWidth: '100%',
  },
  categoryMoreChip: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  categoryExploreGhost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    maxWidth: '100%',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  footerSlot: {
    width: '100%',
    alignSelf: 'stretch',
    marginTop: 8,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
});

export default React.memo(ExploreItemFullPage);
